/**
 * App.js — the Workspace / Analysis page.
 *
 * Phase 3 (Navigation & IA): the shell (identity, navigation, theme,
 * notifications, account, Toaster) lives in `components/UserShell`, and the
 * workspace state lives in `contexts/WorkspaceContext` so that navigating away
 * and back does not throw the user's work away. This file is now only the
 * page: selection screen before the analysis, results after it.
 */
import "@/App.css";
import { Loader2, BarChart3 } from "lucide-react";

import { PortfolioContextBar } from "@/components/PortfolioContextBar";
import { SelectionPanels } from "@/components/SelectionPanels";
import { SelectedEtfsSummary } from "@/components/SelectedEtfsSummary";
import { AddMoreDialog } from "@/components/AddMoreDialog";
import { DataPreview } from "@/components/DataPreview";
import { UserFindHoldingsUrlDialog } from "@/components/UserFindHoldingsUrlDialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { AnalysisProgressCard } from "@/components/AnalysisProgressCard";
import { AnalysisFreshnessBar } from "@/components/AnalysisFreshnessBar";
import { PortfolioActions } from "@/components/portfolios/PortfolioActions";
import { useWorkspace } from "@/contexts/WorkspaceContext";
import { useResultsViewParam } from "@/hooks/useResultsViewParam";
import { STATIC_RESULT_VIEWS } from "@/utils/resultsView";
import { ConfirmDialog, PageHeader } from "@/components/system";
import { WorkspaceStep } from "@/components/WorkspaceStep";
import { formatPercentPoints } from "@/utils/format";
import { useState } from "react";
import { useIsMobile } from "@/hooks/useMediaQuery";

function App() {
  const {
    processedData, setProcessedData, sessionId, activeTab, setActiveTab, etfNames,
    selectedEtfs, selectedIsins, handleSetSelectedIsins, removeEtf,
    atSelectionLimit, notifyLimitHit, MAX_SELECTED_ETFS,
    weights, manualAllocation, totalWeight, isValid, setWeight, resetToEqual,
    knownEtfs, refreshKnownEtfs, pendingIsins, pasteBusy, handlePasteSubmit,
    pastePartial, setPastePartial, pasteFailed, setPasteFailed,
    analysisJob, analysisLocked, processFiles, downloadExcel, resetAll,
    loadPortfolio, analysisBlockedReason,
    analysisMeta, analysisAligned, repeatAnalysis, markPortfolioSaved,
    saveDialogRequest, clearSaveDialogRequest,
    weightConfirmOpen, setWeightConfirmOpen, startAnalysis,
  } = useWorkspace();

  const [guideIsin, setGuideIsin] = useState(null);
  const [addMoreOpen, setAddMoreOpen] = useState(false);

  // Results view state lives in the URL for the views that have a safe identity.
  useResultsViewParam({
    activeTab,
    setActiveTab,
    enabled: Boolean(processedData),
    availableTabs: STATIC_RESULT_VIEWS,
  });

  const isMobile = useIsMobile();

  const runAnalysisButton = (
    <Button
      data-testid="run-analysis-btn"
      onClick={processFiles}
      aria-describedby={analysisBlockedReason ? "run-analysis-reason" : undefined}
      disabled={Boolean(analysisBlockedReason)}
      size="lg"
      className="w-full sm:w-auto bg-brand hover:bg-brand-hover text-content-on-brand disabled:opacity-40"
    >
      {analysisLocked ? (
        <>
          <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" />
          Analisi in corso…
        </>
      ) : (
        <>
          <BarChart3 className="mr-2 h-4 w-4" aria-hidden="true" />
          Esegui analisi
        </>
      )}
    </Button>
  );

  const renderSelectionScreen = () => (
    <div className="space-y-8" data-testid="workspace-selection-screen">
      <PageHeader
        title="Analizza il tuo portafoglio di ETF"
        subtitle="Tre passi: scegli gli ETF, imposta i pesi, avvia l'analisi. Otterrai le holdings consolidate con il peso effettivo di ogni società."
      />

      {atSelectionLimit && (
        <div
          data-testid="selection-limit-banner"
          className="flex flex-wrap items-center justify-between gap-3 px-4 py-3 rounded-lg border border-warning/40 bg-warning-solid/10 text-warning text-sm"
          role="alert"
        >
          <span>
            <strong>Limite raggiunto:</strong> puoi selezionare al massimo{" "}
            {MAX_SELECTED_ETFS} ETF (incolla + catalogo).
            Rimuovi un ETF dal portafoglio per aggiungerne altri.
          </span>
          <Badge className="bg-warning-solid/20 text-warning text-label">
            {selectedEtfs.length}/{MAX_SELECTED_ETFS}
          </Badge>
        </div>
      )}

      <WorkspaceStep
        step={1}
        testId="workspace-step-select"
        title="Scegli gli ETF"
        description="Incolla una lista di ISIN oppure selezionali dal catalogo: i due modi alimentano la stessa selezione."
        done={selectedEtfs.length > 0}
      >
        <SelectionPanels
          onPasteSubmit={handlePasteSubmit}
          pasteBusy={pasteBusy}
          knownEtfs={knownEtfs}
          selectedEtfs={selectedIsins}
          setSelectedEtfs={handleSetSelectedIsins}
          selectionLimitReached={atSelectionLimit}
          onLimitHit={notifyLimitHit}
          disabled={analysisLocked}
        />
      </WorkspaceStep>

      <WorkspaceStep
        step={2}
        testId="workspace-step-weights"
        title="Imposta i pesi"
        description="Quanto pesa ogni ETF nel portafoglio. Di default sono equipesati: modificali se vuoi."
        done={selectedEtfs.length > 0 && isValid}
      >
        <SelectedEtfsSummary
          selectedEtfs={selectedEtfs}
          weights={weights}
          manualAllocation={manualAllocation}
          totalWeight={totalWeight}
          isValid={isValid}
          pendingPasteIsins={pendingIsins}
          pastePartial={pastePartial}
          pasteFailed={pasteFailed}
          onWeightChange={setWeight}
          onRemove={removeEtf}
          onResetEqual={resetToEqual}
          onDismissPartial={() => setPastePartial([])}
          onDismissFailed={() => setPasteFailed([])}
          disabled={analysisLocked}
        />
      </WorkspaceStep>

      <WorkspaceStep
        step={3}
        testId="workspace-step-run"
        title="Avvia l'analisi"
        description="Scarichiamo le holdings di ogni ETF e le consolidiamo in un unico portafoglio."
      >
        <div className="space-y-4">
          {(analysisJob.isRunning
            || analysisJob.job?.status === "cancelled"
            || analysisJob.job?.status === "failed") && (
            <AnalysisProgressCard
              job={analysisJob.job}
              elapsedSec={analysisJob.elapsedSec}
              etaSec={analysisJob.etaSec}
              onCancel={analysisJob.cancel}
              onRetry={processFiles}
            />
          )}
          <div className="flex flex-wrap items-center justify-between gap-3">
            <PortfolioActions
              selectedEtfs={selectedEtfs}
              weights={weights}
              disabled={analysisLocked}
              knownEtfs={knownEtfs}
              sessionId={sessionId}
              openRequest={saveDialogRequest}
              onOpenRequestHandled={clearSaveDialogRequest}
              onSaved={markPortfolioSaved}
            />
            {!isMobile && (
              <div className="flex flex-wrap items-center justify-end gap-3 ml-auto">
                {analysisBlockedReason && (
                  <p
                    id="run-analysis-reason"
                    data-testid="run-analysis-reason"
                    className="text-xs text-content-muted"
                  >
                    {analysisBlockedReason}
                  </p>
                )}
                {runAnalysisButton}
              </div>
            )}
          </div>
        </div>
      </WorkspaceStep>

      {/* Phase 6 — on mobile the CTA follows the user instead of hiding under a
          long list of ETFs. Rendered ONCE (no duplicate control): the desktop
          keeps it inside step 3. */}
      {isMobile && (
        <div
          data-testid="mobile-run-bar"
          className="sticky bottom-0 z-30 -mx-4 mt-2 border-t border-line bg-canvas/95 px-4 pt-3
                     pb-[calc(0.75rem+env(safe-area-inset-bottom))] backdrop-blur"
        >
          {analysisBlockedReason && (
            <p
              id="run-analysis-reason"
              data-testid="run-analysis-reason"
              className="mb-2 text-xs text-content-muted"
            >
              {analysisBlockedReason}
            </p>
          )}
          {runAnalysisButton}
        </div>
      )}
    </div>
  );

  return (
    <div data-testid="workspace-page">
      {processedData ? (
        /* ─────── POST-ANALYSIS: full-width results + portfolio context bar ─────── */
        <div className="app-results">
          <PortfolioContextBar
            selectedEtfs={selectedEtfs}
            sessionId={sessionId}
            downloadExcel={downloadExcel}
            onRemoveEtf={removeEtf}
            onReset={resetAll}
            onOpenAddMore={() => setAddMoreOpen(true)}
            portfolioActions={(
              <PortfolioActions
                selectedEtfs={selectedEtfs}
                weights={weights}
                disabled={analysisLocked}
                knownEtfs={knownEtfs}
                sessionId={sessionId}
                openRequest={saveDialogRequest}
                onOpenRequestHandled={clearSaveDialogRequest}
                onSaved={markPortfolioSaved}
                stacked
              />
            )}
            disabled={analysisLocked}
          />
          <AnalysisFreshnessBar
            analysisMeta={analysisMeta}
            aligned={analysisAligned}
            onRepeat={repeatAnalysis}
            disabled={analysisLocked}
          />
          <div className="app-main-panel">
            <DataPreview
              processedData={processedData}
              setProcessedData={setProcessedData}
              activeTab={activeTab}
              setActiveTab={setActiveTab}
              etfNames={etfNames}
              sessionId={sessionId}
            />
          </div>
          <AddMoreDialog
            open={addMoreOpen}
            onOpenChange={setAddMoreOpen}
            workspaceProps={{
              knownEtfs,
              selectedEtfs: selectedIsins,
              setSelectedEtfs: handleSetSelectedIsins,
              onPasteSubmit: handlePasteSubmit,
              pasteBusy,
              processing: analysisLocked,
              selectionLimitReached: atSelectionLimit,
              onLimitHit: notifyLimitHit,
              processFiles: () => {
                setAddMoreOpen(false);
                processFiles();
              },
            }}
          />
        </div>
      ) : (
        /* ─────── PRE-ANALYSIS: SelectionPanels layout ─────── */
        <div className="app-stack">{renderSelectionScreen()}</div>
      )}

      <ConfirmDialog
        open={weightConfirmOpen}
        onOpenChange={setWeightConfirmOpen}
        testId="run-analysis-confirm"
        title="Avviare l'analisi con un totale diverso da 100 %?"
        description={`La somma dei pesi degli ETF selezionati è ${formatPercentPoints(totalWeight)}.`}
        consequence="L'analisi userà i pesi così come sono: le percentuali dei risultati non si riferiranno a un portafoglio completo."
        confirmLabel="Esegui comunque"
        onConfirm={startAnalysis}
      />

      <UserFindHoldingsUrlDialog
        open={!!guideIsin}
        initialIsin={guideIsin || ""}
        onClose={() => setGuideIsin(null)}
        onCompleted={() => { refreshKnownEtfs(); }}
      />

      {/* Phase 5: the pre-analysis hint belongs to the selection screen only. */}
      {!processedData && (
        <footer className="mt-8 text-center text-content-muted text-sm" data-testid="workspace-footer-hint">
          <p>
            Aggiungi ETF incollando gli ISIN oppure selezionandoli dal
            catalogo. Imposta i pesi e avvia l'analisi.
          </p>
        </footer>
      )}
    </div>
  );
}

export default App;
